import { SimpleCard } from "@gull";
import { RenderSelectInput, RenderTextInput } from "app/common/renderInput";
import { addCharter } from "app/services/charterService";
import { dateValidation } from "app/services/timeDateService";
import { FieldArray, Formik } from "formik";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { MdDelete } from "react-icons/md";
import LaddaButton, { ZOOM_IN } from "react-ladda";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert2";
import {
  model,
  refundableOption,
  tripTypeOptions,
  validationModel,
} from "./Components/Add/charterFlightModel";
import FlightLegs from "./Components/Add/flightLegs/FlightLegs";
import "./CreateFlight.css";

// Constants
const DEFAULT_DAYS = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

const DEFAULT_TRIP_TYPE = {
  value: 1,
  label: "One Way",
};

const INITIAL_FLIGHT_LEG = {
  flightNumber: "",
  airportDepartureId: "",
  airportDestinationId: "",
  flightCarrierId: "",
  checkInBaggage: "",
  fareType: "",
  arrivalTime: "",
  departureTime: "",
};

const AddCharter = () => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    model,
    validationModel,
    allFlightDates: [],
    showDateSelector: false,
    operationType: "dateRange",
    inputDates: "",
    fromDate: "",
    toDate: "",
    selectDaysValues: [],
    index: 0,
    tripType: DEFAULT_TRIP_TYPE,
    valueState: [{ id: 0, label: "" }],
    departureValue: [{ id: 0, label: "" }],
    suggestions: [],
    depSuggestions: [],
    destinationValue: [{ id: 0, label: "" }],
    desSuggestion: [],
    timeDeparture: [{ time: "", timeString: "" }],
    timeArrival: [{ time: "", timeString: "" }],
    valueStateReturn: [{ id: 0, label: "" }],
    suggestionsReturn: [],
    departureValueReturn: [{ id: 0, label: "" }],
    depSuggestionsReturn: [],
    destinationValueReturn: [{ id: 0, label: "" }],
    desSuggestionReturn: [],
    timeDepartureReturn: [{ time: "", timeString: "" }],
    timeArrivalReturn: [{ time: "", timeString: "" }],
    airlineDetails: [{ id: 0, label: "" }],
    airportDetails: [{ id: 0, label: "" }],
  });

  // Memoized values
  const isRoundTrip = useMemo(
    () => state.tripType.value === 2,
    [state.tripType.value],
  );

  // Optimized state update helper
  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Trip type change handler
  const changeTripType = useCallback((type) => {
    setState((prev) => {
      const newState = { ...prev, tripType: type };

      if (type.value === 2) {
        newState.model = {
          ...prev.model,
          flightLegs: {
            ...prev.model.flightLegs,
            return: [{ ...INITIAL_FLIGHT_LEG }],
          },
        };
        newState.returnStarted = type.value;
      }

      return newState;
    });
  }, []);

  // Modal handlers
  const showDateHandler = useCallback(() => {
    updateState({ showDateSelector: true });
  }, [updateState]);

  const handleClose = useCallback(() => {
    updateState({ showDateSelector: false });
  }, [updateState]);

  // Date validation and generation helper
  const generateDatesInRange = useCallback((start, end, filterFn = null) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const dates = [];

    while (startDate.getTime() <= endDate.getTime()) {
      const dateStr = moment(startDate).format("MM/DD/YYYY");
      const shouldInclude = filterFn ? filterFn(startDate) : true;

      if (shouldInclude && dateValidation(dateStr)) {
        dates.push(moment(startDate).format("YYYY-MM-DD"));
      }

      startDate.setDate(startDate.getDate() + 1);
    }

    return dates;
  }, []);

  // Close modal helper
  const closeModal = useCallback(() => {
    setTimeout(() => {
      document.getElementsByClassName("close")[0]?.click();
    }, 10);
  }, []);

  // Date range submit
  const dateRangeSubmit = useCallback(() => {
    const { fromDate, toDate } = state;

    if (!fromDate || !toDate) {
      NotificationManager.error("Please select both start and end dates");
      return;
    }

    const dates = generateDatesInRange(fromDate, toDate);

    if (dates.length > 0) {
      updateState({ allFlightDates: dates });
      closeModal();
    }
  }, [
    state.fromDate,
    state.toDate,
    generateDatesInRange,
    updateState,
    closeModal,
  ]);

  // Weekly dates submit
  const weeklyDatesSubmit = useCallback(
    (date1, date2, days) => {
      if (!date1 || !date2 || !days || days.length === 0) {
        NotificationManager.error("Please select date range and days of week");
        return;
      }

      const dates = generateDatesInRange(date1, date2, (date) =>
        days.includes(date.getDay()),
      );

      if (dates.length > 0) {
        updateState({ allFlightDates: dates });
        closeModal();
      }
    },
    [generateDatesInRange, updateState, closeModal],
  );

  // Specific dates submit
  const datesSubmit = useCallback(() => {
    const inputDates = state.inputDates?.trim();

    if (!inputDates) {
      NotificationManager.error("Please select dates");
      return;
    }

    const dateLines = inputDates.split("\n");
    const validDates = dateLines
      .map((line) => line.trim())
      .filter(Boolean)
      .map((dateStr) => {
        const validationFormat = dateStr.includes("/")
          ? dateStr
          : moment(dateStr).format("MM/DD/YYYY");

        if (moment(dateStr).isValid() && dateValidation(validationFormat)) {
          return moment(dateStr).format("YYYY-MM-DD");
        }
        return null;
      })
      .filter(Boolean);

    if (validDates.length > 0) {
      updateState({ allFlightDates: validDates });
      closeModal();
    } else {
      NotificationManager.error("No valid dates found");
    }
  }, [state.inputDates, updateState, closeModal]);

  // Delete date handler
  const handleDeleteContact = useCallback((date) => {
    setState((prev) => ({
      ...prev,
      allFlightDates: prev.allFlightDates.filter((d) => d !== date),
    }));
  }, []);

  // Array manipulation helpers
  const createArrayPusher = useCallback(
    (key) => () => {
      setState((prev) => ({
        ...prev,
        [key]: [...prev[key], prev[key][0]],
      }));
    },
    [],
  );

  const changeCarrierValues = createArrayPusher("valueState");
  const changeDepartureValues = createArrayPusher("departureValue");
  const changeDestinationValues = createArrayPusher("destinationValue");

  // Time input handlers
  const createTimeInputHandler = useCallback(
    (key) => (time, timeString, index) => {
      setState((prev) => {
        const newValues = [...prev[key]];
        newValues[index] = { time, timeString };
        return { ...prev, [key]: newValues, index };
      });
    },
    [],
  );

  const changeTimeDepartureInput = createTimeInputHandler("timeDeparture");
  const changeTimeArrivalInput = createTimeInputHandler("timeArrival");

  const changeTimeDepartureValues = createArrayPusher("timeDeparture");
  const changeTimeArrivalValues = createArrayPusher("timeArrival");

  // Form submission
  const submitForm = useCallback(
    async (values) => {
      try {
        const { tripType, allFlightDates } = state;

        if (allFlightDates.length === 0) {
          NotificationManager.error("Please enter all details!");
          return;
        }

        const flightLegs = values.flightLegs.onward.map((leg) => ({ ...leg }));

        const sendData = {
          ...values,
          tripType: tripType.label,
          tripTypeId: tripType.value,
          flightDates: allFlightDates,
          flightLegs,
          pnr: values.pnr?.toUpperCase(),
        };

        const result = await addCharter(sendData);

        if (result.status === "Success") {
          NotificationManager.success(result.message, "Created!", 5000);
          navigate("/admin/flight/list");
        } else {
          NotificationManager.error(
            "Something went wrong please try again after sometime.",
          );
        }
      } catch (error) {
        NotificationManager.error("Please enter all details!");
      }
    },
    [state.tripType, state.allFlightDates, navigate],
  );

  // Date deletion confirmation
  const confirmDeleteDate = useCallback(
    (date) => {
      swal
        .fire({
          title: "Remove Date?",
          text: "This date will be removed from the schedule",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, remove it",
          cancelButtonText: "Cancel",
        })
        .then((result) => {
          if (result.value) {
            handleDeleteContact(date);
          } else {
            swal.fire({
              title: "Cancelled",
              text: "Date retained in schedule",
              icon: "info",
              timer: 1500,
            });
          }
        });
    },
    [handleDeleteContact],
  );

  return (
    <div className="container-fluid px-4 py-3">
      <Helmet>
        <title>Create Series Flight</title>
      </Helmet>

      <Formik
        onSubmit={submitForm}
        initialValues={model}
        validationSchema={validationModel}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form className="needs-validation" onSubmit={handleSubmit} noValidate>
            {/* Trip Type Selection */}
            <div className="row mb-4">
              <div className="col-12">
                <SimpleCard title="Trip Type Selection" className="mb-4">
                  <div className="form-row">
                    <label className="mb-2">
                      Select Trip Type <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="tripType"
                      classNamePrefix="react-select"
                      options={tripTypeOptions}
                      value={state.tripType}
                      onChange={changeTripType}
                      placeholder="Select trip type"
                    />
                  </div>
                </SimpleCard>
              </div>
            </div>

            {/* Flight Legs Section */}
            <div className="row mb-4">
              <Col md={isRoundTrip ? "6" : "12"}>
                <Card className="p-4 mb-4 border shadow-sm">
                  <Card.Title className="mb-3 titleMain">
                    Flight Data - Onward
                  </Card.Title>

                  <FieldArray
                    name="flightLegs.onward"
                    render={(arrayHelpers) => (
                      <>
                        {values.flightLegs.onward?.map((leg, index) => (
                          <div key={index} className="mb-3">
                            <FlightLegs
                              index={index}
                              arrayHelpers={arrayHelpers}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              changeTimeDepartureInput={
                                changeTimeDepartureInput
                              }
                              timeDeparture={state.timeDeparture}
                              timeArrival={state.timeArrival}
                              changeTimeArrivalInput={changeTimeArrivalInput}
                              tripType={state.tripType.value}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                        ))}
                        <Row className="px-4">
                          <Col lg={isRoundTrip ? "9" : "11"}></Col>
                          <Col sm="6" lg={isRoundTrip ? "3" : "1"}>
                            <Button
                              className="mb-4 d-none"
                              variant="primary"
                              type="button"
                              onClick={() => {
                                arrayHelpers.push({ ...INITIAL_FLIGHT_LEG });
                                changeCarrierValues();
                                changeDepartureValues();
                                changeDestinationValues();
                                changeTimeDepartureValues();
                                changeTimeArrivalValues();
                              }}
                            >
                              Add Leg
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  />
                </Card>
              </Col>
            </div>

            {/* Pricing and Dates Section */}
            <div className="row mb-4">
              <div className="col-md-12 col-12">
                <div className="row mb-4">
                  <div className="col-12">
                    <SimpleCard className="mb-4">
                      <h5 className="mb-3">
                        Flight Schedule
                        <span className="text-danger"> *</span>
                      </h5>

                      <button
                        className="btn btn-primary mb-3 w-100"
                        onClick={showDateHandler}
                        type="button"
                      >
                        Select Flight Dates
                      </button>

                      {state.allFlightDates.length > 0 ? (
                        <div className="table-wrapper-scroll-y my-custom-scrollbar">
                          <table className="table table-sm table-hover table-bordered">
                            <thead>
                              <tr>
                                <th scope="col">Flight Date</th>
                                <th
                                  scope="col"
                                  className="text-center"
                                  style={{ width: "100px" }}
                                >
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {state.allFlightDates.map((date, key) => (
                                <tr key={key}>
                                  <td>
                                    <strong>
                                      {moment(date).format("MMM DD, YYYY")}
                                    </strong>
                                    <small className="d-block text-muted">
                                      {moment(date).format("dddd")}
                                    </small>
                                  </td>
                                  <td className="text-center">
                                    <div className="cursor-pointer">
                                      <MdDelete
                                        className="text-danger"
                                        size={20}
                                        onClick={() => confirmDeleteDate(date)}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <p className="mb-0">No dates selected yet</p>
                          <small>
                            Click the button above to add flight dates
                          </small>
                        </div>
                      )}

                      <small className="text-danger">
                        {touched.flightDates &&
                          state.allFlightDates.length === 0 &&
                          "Please select at least one flight date"}
                      </small>
                    </SimpleCard>
                  </div>
                </div>
              </div>

              <div className="col-md-12 col-12">
                <SimpleCard title="Seat Booking & Pricing" className="mb-4">
                  <Row className="form-row">
                    <Col xs={12} lg={12}>
                      <div className="d-flex align-items-center gap-3">
                        <label className="mb-2">
                          Live PNR Available?{" "}
                          {values.isPnrRequired && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="isPnrRequired"
                            id="pnrYes"
                            checked={values.isPnrRequired === true}
                            onChange={() =>
                              setFieldValue("isPnrRequired", true)
                            }
                          />
                          <label className="form-check-label" htmlFor="pnrYes">
                            Yes
                          </label>
                        </div>

                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="isPnrRequired"
                            id="pnrNo"
                            checked={values.isPnrRequired === false}
                            onChange={() =>
                              setFieldValue("isPnrRequired", false)
                            }
                          />
                          <label className="form-check-label" htmlFor="pnrNo">
                            No
                          </label>
                        </div>
                      </div>
                      {values.isPnrRequired && (
                        <div className="mt-3">
                          <RenderTextInput
                            type="text"
                            name="pnr"
                            display="PNR Number"
                            value={values?.pnr}
                            error={errors.pnr}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            touched={touched.pnr}
                            placeholder="PNR Number"
                          />
                        </div>
                      )}
                    </Col>
                    <Col xs={12} lg={6}>
                      <RenderTextInput
                        placeholder="Available Seats"
                        type="number"
                        name="seats"
                        display="Available Seats"
                        value={values.seats}
                        error={errors.seats}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.seats}
                        required
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <RenderSelectInput
                        type="text"
                        display="Refundable Booking"
                        name="refundable"
                        items={refundableOption}
                        value={values.refundable}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Col>
                  </Row>

                  {/* Adult Fare */}
                  <h5 className="mt-4">Adult Fare (Per Seat)</h5>
                  <Row className="form-row">
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Base Fare"
                        name="adultBaseFare"
                        value={values.adultBaseFare}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.adultBaseFare}
                        touched={touched.adultBaseFare}
                        required
                        placeholder="Total Base Fare"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Tax"
                        name="adultTotalTaxPerSeat"
                        value={values.adultTotalTaxPerSeat}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.adultTotalTaxPerSeat}
                        touched={touched.adultTotalTaxPerSeat}
                        required
                        placeholder="Total Tax"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Fare (Base Fare +Tax)"
                        value={
                          values.adultTotalTaxPerSeat + values.adultBaseFare
                        }
                        readOnly
                      />
                    </Col>
                  </Row>

                  {/* Child Fare */}
                  <h5 className="mt-4">Child Fare (Per Seat)</h5>
                  <Row className="form-row">
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Base Fare"
                        name="childBaseFare"
                        value={values.childBaseFare}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.childBaseFare}
                        touched={touched.childBaseFare}
                        required
                        placeholder="Total Base Fare"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Tax"
                        name="childTotalTaxPerSeat"
                        value={values.childTotalTaxPerSeat}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.childTotalTaxPerSeat}
                        touched={touched.childTotalTaxPerSeat}
                        required
                        placeholder="Total Tax"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Fare (Base Fare +Tax)"
                        value={
                          values.childTotalTaxPerSeat + values.childBaseFare
                        }
                        readOnly
                      />
                    </Col>
                  </Row>

                  {/* Infant Fare */}
                  <h5 className="mt-4">Infant Fare (Per Seat)</h5>
                  <Row className="form-row">
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Base Fare"
                        name="infrantBaseFare"
                        value={values.infrantBaseFare}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.infrantBaseFare}
                        touched={touched.infrantBaseFare}
                        required
                        placeholder="Total Base Fare"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Tax"
                        name="infrantTotalTaxPerSeat"
                        value={values.infrantTotalTaxPerSeat}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.infrantTotalTaxPerSeat}
                        touched={touched.infrantTotalTaxPerSeat}
                        required
                        placeholder="Total Tax"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <RenderTextInput
                        type="number"
                        display="Total Fare (Base Fare +Tax)"
                        value={
                          values.infrantTotalTaxPerSeat + values.infrantBaseFare
                        }
                        readOnly
                      />
                    </Col>
                  </Row>
                </SimpleCard>
              </div>

              <div className="row">
                <div className="col-12">
                  <SimpleCard title="Send Confirmation" className="mb-4">
                    <p className="text-muted small mb-3">
                      Booking confirmations will be sent to these email
                      addresses
                    </p>
                    <Row className="form-row">
                      <Col md={6}>
                        <RenderTextInput
                          type="email"
                          name="commEmailOne"
                          display="Primary Contact"
                          placeholder="email@example.com"
                          value={values.commEmailOne}
                          error={errors.commEmailOne}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          touched={touched.commEmailOne}
                        />
                      </Col>
                      <Col md={6}>
                        <RenderTextInput
                          type="email"
                          name="commEmailTwo"
                          display="Secondary Contact"
                          placeholder="email@example.com"
                          value={values.commEmailTwo}
                          error={errors.commEmailTwo}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          touched={touched.commEmailTwo}
                        />
                      </Col>
                    </Row>
                  </SimpleCard>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="row">
              <div className="col-12 text-center">
                <LaddaButton
                  loading={isSubmitting}
                  data-style={ZOOM_IN}
                  className="btn btn-primary mb-4 px-5 py-3"
                  type="submit"
                >
                  Create Series Booking
                </LaddaButton>
              </div>
            </div>

            {/* Date Selection Modal */}
            <Modal
              show={state.showDateSelector}
              backdropClassName={false}
              backdrop="static"
              keyboard={false}
              onHide={handleClose}
              centered
              size="md"
            >
              <Modal.Header>
                <h4 className="text-primary mb-0">Select Flight Dates</h4>
                <button
                  type="button"
                  onClick={handleClose}
                  className="close btn btn-sm"
                  data-dismiss="modal"
                >
                  <div style={{ fontSize: "18px", fontWeight: "600" }}>×</div>
                </button>
              </Modal.Header>
              <div className="modal-body">
                <div className="row mb-2">
                  <div className="col-12">
                    <label className="mb-2 d-block">
                      <strong>Selection Method</strong>
                    </label>
                  </div>
                  <div className="col-12">
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input"
                        value="dates"
                        name="operationType"
                        id="dates"
                        checked={state.operationType === "dates"}
                        onChange={(e) =>
                          updateState({ operationType: e.target.value })
                        }
                      />
                      <label htmlFor="dates" className="form-check-label">
                        Specific Dates
                      </label>
                    </div>

                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input"
                        value="weeklyTurn"
                        name="operationType"
                        id="weeklyTurn"
                        checked={state.operationType === "weeklyTurn"}
                        onChange={(e) =>
                          updateState({ operationType: e.target.value })
                        }
                      />
                      <label htmlFor="weeklyTurn" className="form-check-label">
                        Weekly Schedule
                      </label>
                    </div>

                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input"
                        value="dateRange"
                        name="operationType"
                        id="dateRange"
                        checked={state.operationType === "dateRange"}
                        onChange={(e) =>
                          updateState({ operationType: e.target.value })
                        }
                      />
                      <label htmlFor="dateRange" className="form-check-label">
                        Date Range
                      </label>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    {state.operationType === "dates" && (
                      <div className="bg-light p-2 rounded">
                        <label htmlFor="inputDates" className="mb-2">
                          <strong>Enter Flight Dates</strong>
                          <span className="text-danger"> *</span>
                        </label>
                        <p className="small text-muted mb-3">
                          Enter dates in YYYY-MM-DD format, one per line
                          <br />
                          Example: {moment().format("YYYY-MM-DD")}
                        </p>
                        <textarea
                          className="form-control"
                          style={{ resize: "none" }}
                          name="inputDates"
                          id="inputDates"
                          value={state.inputDates}
                          onChange={(e) =>
                            updateState({ inputDates: e.target.value })
                          }
                          cols="40"
                          rows="4"
                          placeholder={`${moment().format("YYYY-MM-DD")}\n${moment().add(1, "day").format("YYYY-MM-DD")}\n${moment().add(2, "day").format("YYYY-MM-DD")}`}
                          required
                        />
                      </div>
                    )}

                    {state.operationType === "weeklyTurn" && (
                      <div className="bg-light p-2 rounded">
                        <label className="mb-2">
                          <strong>Select Days of Week</strong>
                        </label>
                        <Select
                          name="defaultDays"
                          options={DEFAULT_DAYS}
                          isMulti
                          className="basic-multi-select mb-3"
                          classNamePrefix="select"
                          placeholder="Choose days..."
                          onChange={(values) => {
                            const daysValues = values.map(
                              ({ value }) => value * 1,
                            );
                            if (daysValues.length > 0) {
                              updateState({ selectDaysValues: daysValues });
                            }
                          }}
                        />
                        <Row>
                          <Col md={6}>
                            <label htmlFor="fromDate" className="mb-2">
                              <strong>Start Date</strong>
                            </label>
                            <input
                              id="fromDate"
                              type="date"
                              min={moment().format("YYYY-MM-DD")}
                              onChange={(e) =>
                                updateState({ fromDate: e.target.value })
                              }
                              value={state.fromDate}
                              name="fromDate"
                              className="form-control"
                            />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="toDate" className="mb-2">
                              <strong>End Date</strong>
                            </label>
                            <input
                              id="toDate"
                              type="date"
                              min={moment(state.fromDate).format("YYYY-MM-DD")}
                              onChange={(e) =>
                                updateState({ toDate: e.target.value })
                              }
                              value={state.toDate}
                              name="toDate"
                              className="form-control"
                            />
                          </Col>
                        </Row>
                      </div>
                    )}

                    {state.operationType === "dateRange" && (
                      <div className="bg-light p-2 rounded">
                        <Row>
                          <Col md={6}>
                            <label htmlFor="fromDate" className="mb-2">
                              <strong>Start Date</strong>
                            </label>
                            <input
                              id="fromDate"
                              type="date"
                              min={moment().format("YYYY-MM-DD")}
                              onChange={(e) =>
                                updateState({ fromDate: e.target.value })
                              }
                              value={state.fromDate}
                              name="fromDate"
                              className="form-control"
                            />
                          </Col>

                          <Col md={6}>
                            <label htmlFor="toDate" className="mb-2">
                              <strong>End Date</strong>
                            </label>
                            <input
                              id="toDate"
                              type="date"
                              min={moment(state.fromDate).format("YYYY-MM-DD")}
                              onChange={(e) =>
                                updateState({ toDate: e.target.value })
                              }
                              value={state.toDate}
                              name="toDate"
                              className="form-control"
                            />
                          </Col>
                        </Row>
                      </div>
                    )}

                    {state.operationType && (
                      <Row className="mt-4">
                        <Col xs={6}>
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                              if (state.operationType === "dates") {
                                datesSubmit();
                              } else if (state.operationType === "weeklyTurn") {
                                weeklyDatesSubmit(
                                  state.fromDate,
                                  state.toDate,
                                  state.selectDaysValues,
                                );
                              } else if (state.operationType === "dateRange") {
                                dateRangeSubmit();
                              }
                            }}
                          >
                            ✓ Apply Dates
                          </button>
                        </Col>
                        <Col xs={6}>
                          <Button
                            onClick={handleClose}
                            className="w-100"
                            variant="outline-primary"
                          >
                            Cancel
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </div>
                </div>
              </div>
            </Modal>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default AddCharter;
